import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar.jsx';
import { api } from '../api/client.js';

/**
 * Mask a key ID for display: keep first 8 chars + last 4, hide the middle.
 *   "apikey_01ABCXYZ123456789" → "apikey_0...6789"
 */
function maskKey(id) {
  if (!id) return '—';
  if (id.length < 16) return '•'.repeat(id.length);
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export default function AnthropicSettings() {
  const [keys, setKeys] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [revealed, setRevealed] = useState({});       // { keyId: true } when shown
  const [copied, setCopied] = useState(null);         // keyId that was just copied

  useEffect(() => {
    api.get('/api/anthropic/api-keys')
      .then((r) => setKeys(r.data.keys))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  const triggerSync = async (hoursBack) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post('/api/anthropic/sync', { hoursBack });
      setSyncResult(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const toggleReveal = (id) => {
    setRevealed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard API not available — silently fail
    }
  };

  return (
    <>
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-medium mb-1">Anthropic integration</h1>
        <p className="text-sm text-muted mb-6">
          View your organization's Anthropic API keys and trigger manual usage syncs.
        </p>

        <div className="bg-white border border-border rounded-xl p-6 mb-6">
          <h2 className="text-base font-medium mb-2">Manual sync</h2>
          <p className="text-xs text-muted mb-3">
            Pulls recent usage from the Anthropic Admin API into your local UsageEvent table.
            Auto-runs every 10 min in the background.
          </p>
          <div className="flex gap-2">
            <button onClick={() => triggerSync(2)} disabled={syncing}
              className="text-sm border border-border rounded-lg px-3 py-1.5 hover:bg-neutral-50 disabled:opacity-50">
              Sync last 2h
            </button>
            <button onClick={() => triggerSync(24)} disabled={syncing}
              className="text-sm border border-border rounded-lg px-3 py-1.5 hover:bg-neutral-50 disabled:opacity-50">
              Sync last 24h
            </button>
            <button onClick={() => triggerSync(168)} disabled={syncing}
              className="text-sm border border-border rounded-lg px-3 py-1.5 hover:bg-neutral-50 disabled:opacity-50">
              Sync last 7d
            </button>
          </div>
          {syncing && <p className="text-xs text-muted mt-3">Syncing — this can take 10-60 sec…</p>}
          {syncResult && (
            <div className="mt-3 p-3 bg-neutral-50 rounded-lg text-xs">
              <p>✓ Synced <strong>{syncResult.synced}</strong> events across <strong>{syncResult.apps}</strong> mapped apps.
              {syncResult.skipped ? ` ${syncResult.skipped} bucket rows skipped (no app mapping).` : ''}</p>
            </div>
          )}
          {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
        </div>

        <h2 className="text-base font-medium mb-1">Your Anthropic API keys</h2>
        <p className="text-xs text-muted mb-3">
          These are key <em>identifiers</em> (not the secret keys). Click reveal to see the full ID,
          or copy it directly. Paste a key ID into an app's "Anthropic API key ID" field to link them.
        </p>

        {loading && <p className="text-muted">Loading…</p>}

        {!loading && keys.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            No keys returned. Either ANTHROPIC_ADMIN_API_KEY isn't set, or your org has no API keys yet.
          </div>
        )}

        {keys.length > 0 && (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-muted">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Key ID</th>
                  <th className="text-left px-4 py-2.5 font-medium">Workspace</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const isRevealed = !!revealed[k.id];
                  return (
                    <tr key={k.id} className="border-t border-border">
                      <td className="px-4 py-2.5">{k.name}</td>
                      <td className="px-4 py-2.5">
                        <code className="text-xs bg-neutral-100 px-1.5 py-0.5 rounded font-mono">
                          {isRevealed ? k.id : maskKey(k.id)}
                        </code>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted">
                        {k.workspaceId ? (
                          <code className="font-mono">
                            {revealed[`ws-${k.id}`] ? k.workspaceId : maskKey(k.workspaceId)}
                          </code>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                          k.status === 'active'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-neutral-100 text-muted'
                        }`}>
                          {k.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => toggleReveal(k.id)}
                          className="text-xs text-muted hover:text-ink mr-3"
                          title={isRevealed ? 'Hide' : 'Reveal'}
                        >
                          {isRevealed ? 'Hide' : 'Reveal'}
                        </button>
                        <button
                          onClick={() => copyToClipboard(k.id)}
                          className="text-xs text-muted hover:text-ink"
                          title="Copy full ID to clipboard"
                        >
                          {copied === k.id ? '✓ Copied' : 'Copy'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-muted mt-4">
          Note: These are non-secret identifiers used by Anthropic to reference an API key.
          The actual secret keys (<code className="font-mono">sk-ant-…</code>) are managed in the Anthropic Console
          and never stored in this Launchpad.
        </p>
      </main>
    </>
  );
}
